// Renders either the Plaid or Argyle verification tree based on the per-scope
// feature flag resolution from /api/income-verification/provider.
//
// Default behavior (when the resolver returns 'plaid' or is unavailable) is to
// render <VerifyThroughPlaid> — existing applicants see zero change. When the
// resolver returns 'argyle', <VerifyThroughArgyle> is rendered instead.
//
// `provider: 'none'` renders nothing (feature is disabled for this scope).
import React, { CSSProperties, ReactNode } from 'react';
import { useGetOne } from 'react-admin';
import VerifyThroughPlaid from './VerifyThroughPlaid';
import VerifyThroughArgyle from './VerifyThroughArgyle';

type Feature = 'bank_income' | 'payroll_income';
type LinkType = 'bank' | 'payroll';

type Props = {
  trn_id?: string | null;
  linkType?: LinkType;
  triggerLabel?: string;
  includeRelinkWarning?: boolean;
  onDone?: () => void;
  sourceId?: number;
  triggerContainerClassName?: string;
  triggerContainerStyle?: CSSProperties;
  triggerRowClassName?: string;
  triggerRowStyle?: CSSProperties;
  triggerButtonClassName?: string;
  triggerButtonStyle?: CSSProperties;
  triggerIcon?: ReactNode;
  disabled?: boolean;
  onFlowStarted?: () => void;
  onProviderSuccess?: () => void;
  onFlowExited?: (completed: boolean) => void;
};

const LINK_TYPE_TO_FEATURE: Record<LinkType, Feature> = {
  bank: 'bank_income',
  payroll: 'payroll_income',
};

export const ProviderToggle: React.FC<Props> = ({
  trn_id,
  linkType = 'bank',
  onProviderSuccess,
  ...rest
}) => {
  const feature = LINK_TYPE_TO_FEATURE[linkType];

  const { data, isLoading, isError } = useGetOne(
    'incomeVerificationProvider',
    { id: trn_id ?? '', meta: { feature } },
    { enabled: trn_id != null }
  );

  if (!trn_id) return null;

  // While resolving, stay silent. Both <VerifyThroughX> components are gated
  // on their own enablement call, so rendering nothing here is safe.
  if (isLoading) return null;

  // Resolver-error degradation (review-new.md P2): when the resolver call
  // fails (transient 500, network blip, etc.), don't pick a single provider
  // — picking 'plaid' would hide the CTA entirely on Argyle-only scopes
  // (VerifyThroughPlaid would self-hide because Plaid's enablement check
  // returns false). Render BOTH provider components and let each one's own
  // enablement check decide whether to surface. Whichever scope is enabled
  // wins; if neither is enabled, both render null — same outcome as
  // provider='none'.
  if (isError) {
    return (
      <>
        <VerifyThroughArgyle
          trn_id={trn_id}
          linkType={linkType}
          onArgyleSuccess={onProviderSuccess}
          {...rest}
        />
        <VerifyThroughPlaid
          trn_id={trn_id}
          linkType={linkType}
          onPlaidSuccess={onProviderSuccess}
          {...rest}
        />
      </>
    );
  }

  const provider = data?.provider || 'plaid';

  if (provider === 'none') return null;

  if (provider === 'argyle') {
    return (
      <VerifyThroughArgyle
        trn_id={trn_id}
        linkType={linkType}
        onArgyleSuccess={onProviderSuccess}
        {...rest}
      />
    );
  }

  return (
    <VerifyThroughPlaid
      trn_id={trn_id}
      linkType={linkType}
      onPlaidSuccess={onProviderSuccess}
      {...rest}
    />
  );
};

export default ProviderToggle;
